import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useModelService } from 'services/model/model.service';
import { DataResult } from 'models/data';
import { useAppStore } from 'hooks/state/useAppStore';
import { Interpretation } from 'components/MeasureComparison';
import { CategoriesInfoContent } from './components/CategoriesInfoContent';
import {
  PerformanceGapDisplay,
  PerformanceGapEmptyState,
} from 'pages/Benchmarking/common/performance-gap/PerformanceGapDisplay';
import { getCombinedBenchmarkEmptyStateType } from 'pages/Benchmarking/common/performance-gap/getBenchmarkEmptyStateType';
import { useBenchmarkDataResult } from 'pages/Benchmarking/queryHooks';
import { getErrorMessage } from 'utils/getErrorMessage';

interface CategoriesDashboardInfo {
  data: DataResult;
  benchmark: string;
}

export function CategoriesDashboard() {
  const { getMeasures, getAttribute } = useModelService();

  const prdCategory = getAttribute('prdCategory');
  const { benchmark } = useAppStore();

  const [dashboardData, setDashboardData] = useState<CategoriesDashboardInfo>();

  const { categories } = useAppStore();

  const selectedCategoryCodes = (categories ?? []).map(category => category.toString());

  const { data: inflightDashboardData, isPending, isError, error } = useBenchmarkDataResult('categories');

  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const categoryDashboardInfo = {
      titleKey: 'Categories Benchmarking',
      subtitleKey: 'info.content.all_dates_utc',
      content: <CategoriesInfoContent />,
    };

    setContent(categoryDashboardInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  useEffect(() => {
    if (inflightDashboardData) {
      const filteredCategories = inflightDashboardData.sets.categories.filter(category =>
        selectedCategoryCodes.length > 0 && category.attributes.prdCategory
          ? selectedCategoryCodes.includes(category.attributes.prdCategory)
          : true
      );
      setDashboardData({
        ...inflightDashboardData,
        benchmark: benchmark,
        data: {
          ...inflightDashboardData,
          sets: {
            ...inflightDashboardData?.sets,
            categories: filteredCategories,
          },
        },
      });
    }
  }, [inflightDashboardData]);

  if (!dashboardData && !isError) {
    return <MetricsLoadingSpinner />;
  }

  if (isError) {
    const errorMessage = getErrorMessage(error);
    return (
      <PageContainer>
        <Tile
          titleId="benchmarking.categories"
          errorDetails={errorMessage}
          testId="benchmarking-categories-dashboard-error"
        />
      </PageContainer>
    );
  }

  if (!dashboardData) {
    return <MetricsLoadingSpinner />;
  }

  const [ordGmvVal, retQtyRateOD, cncQtyRate] = getMeasures(['ordGmvVal', 'retQtyRateExpOD', 'cncQtyRate']);

  const dataSet = dashboardData.data.sets.categories;
  const combinedReturnsAndCancellationsEmptyState = getCombinedBenchmarkEmptyStateType(
    dataSet,
    [retQtyRateOD, cncQtyRate],
    dashboardData.benchmark
  );

  return (
    <PageContainer>
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.additional.performance" />
      </PageSectionTitle>

      <PerformanceGapDisplay
        dataSet={dataSet}
        improvementMeasure={ordGmvVal}
        groupingAttribute={prdCategory}
        interpretation={Interpretation.POSITIVE}
        benchmark={dashboardData.benchmark}
        isPending={isPending}
        opportunitiesTestId="bm-categories-growth-opportunities"
        chartTileTestId="bm-categories-growth-chart"
        chartTestId="bm-categories-ordered-gmv-growth"
      />

      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.returns-canellation" />
      </PageSectionTitle>

      {combinedReturnsAndCancellationsEmptyState ? (
        <PerformanceGapEmptyState
          type={combinedReturnsAndCancellationsEmptyState}
          testId="bm-categories-returns-cancellations-empty"
        />
      ) : (
        <>
          <PerformanceGapDisplay
            dataSet={dataSet}
            improvementMeasure={retQtyRateOD}
            groupingAttribute={prdCategory}
            interpretation={Interpretation.NEGATIVE}
            benchmark={dashboardData.benchmark}
            isPending={isPending}
            opportunitiesTestId="bm-categories-returns-opportunities"
            chartTileTestId="bm-categories-returns-chart"
            chartTestId="bm-categories-returns-chart-plot"
          />
          <PerformanceGapDisplay
            dataSet={dataSet}
            improvementMeasure={cncQtyRate}
            groupingAttribute={prdCategory}
            interpretation={Interpretation.NEGATIVE}
            benchmark={dashboardData.benchmark}
            isPending={isPending}
            opportunitiesTestId="bm-categories-cancellations-opportunities"
            chartTileTestId="bm-categories-cancellations-chart"
            chartTestId="bm-categories-cancellations-chart-plot"
          />
        </>
      )}
    </PageContainer>
  );
}
